import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { CSSProperties } from 'vue'

import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  aboutCollapsedWidth,
  aboutExpandedWidth,
  aboutGap,
  creatorExpandedBox,
  creatorExpandedRightX,
  creatorSmallBoxes,
  creatorSmallX,
} from '../data/home2026'

export const useStageScale = () => {
  const stageScale = ref(1)
  const stageWidth = ref(DESIGN_WIDTH)
  const stageHeight = ref(DESIGN_HEIGHT)
  let resizeFrame: number | undefined

  const scaledPx = (value: number) => `${Number((value * stageScale.value).toFixed(3))}px`

  const stageVars = computed(
    () =>
      ({
        '--stage-width': `${stageWidth.value}px`,
        '--stage-height': `${stageHeight.value}px`,
        '--hero-top': scaledPx(170),
        '--hero-height': scaledPx(740),
        '--hero-card-y': scaledPx(0),
        '--hero-card-height': scaledPx(630),
        '--hero-card-center-left': scaledPx(400),
        '--hero-card-center-width': scaledPx(1120),
        '--hero-card-hidden-left': scaledPx(770),
        '--hero-card-side-left': scaledPx(0),
        '--hero-card-side-width': scaledPx(380),
        '--hero-card-side-gap': scaledPx(20),
        '--hero-card-right-left': scaledPx(1540),
        '--hero-dot-y': scaledPx(680),
        '--hero-dot-gap': scaledPx(10),
        '--hero-dot-width': scaledPx(30),
        '--hero-dot-active-width': scaledPx(30),
        '--hero-dot-height': scaledPx(10),
        '--hero-perspective': scaledPx(1400),
        '--radius-30': scaledPx(30),
        '--hero-shadow-y': scaledPx(10),
        '--hero-shadow-blur': scaledPx(20),
        '--shadow-y': scaledPx(34),
        '--shadow-blur': scaledPx(36),
        '--feature-top': scaledPx(3399),
        '--feature-height': scaledPx(930),
        '--feature-fade-width': scaledPx(834),
        '--feature-gap': scaledPx(11),
        '--feature-card-width': scaledPx(430),
        '--feature-start': scaledPx(-185),
        '--feature-end': scaledPx(-2390),
        '--focus-outline-width': scaledPx(3),
        '--focus-outline-offset': scaledPx(4),
        '--video-radius': scaledPx(8),
        '--expand-card-radius': scaledPx(30),
        '--expand-card-shadow-y': scaledPx(24),
        '--expand-card-shadow-blur': scaledPx(30),
        '--about-expanded-width': scaledPx(aboutExpandedWidth),
        '--about-collapsed-width': scaledPx(aboutCollapsedWidth),
        '--about-gap': scaledPx(aboutGap),
        '--about-title-font': scaledPx(50),
        '--about-title-line': scaledPx(70),
        '--about-padding-x': scaledPx(50),
        '--about-padding-top': scaledPx(50),
        '--about-mark-font': scaledPx(24),
        '--about-copy-top': scaledPx(47),
        '--about-copy-font': scaledPx(35),
        '--about-copy-line': scaledPx(49),
        '--about-collapsed-mark-font': scaledPx(34),
        '--creator-expanded-width': scaledPx(creatorExpandedBox.w),
        '--creator-expanded-height': scaledPx(creatorExpandedBox.h),
        '--creator-expanded-right-x': scaledPx(creatorExpandedRightX),
        '--creator-small-x': scaledPx(creatorSmallX),
        '--creator-small-second-top': scaledPx(creatorSmallBoxes[1].y),
        '--creator-small-width': scaledPx(creatorSmallBoxes[0].w),
        '--creator-small-height': scaledPx(creatorSmallBoxes[0].h),
        '--creator-padding-x': scaledPx(100),
        '--creator-padding-top': scaledPx(100),
        '--creator-title-font': scaledPx(50),
        '--creator-title-line': scaledPx(70),
        '--creator-copy-top': scaledPx(250),
        '--creator-copy-font': scaledPx(40),
        '--creator-copy-line': scaledPx(56),
        '--story-copy-gap': scaledPx(58),
        '--story-copy-font': scaledPx(28),
        '--story-copy-line': scaledPx(36),
        '--faq-content-width': scaledPx(1120),
        '--faq-side-padding': scaledPx(400),
        '--faq-padding-top': scaledPx(59),
        '--faq-title-font': scaledPx(50),
        '--faq-title-line': scaledPx(70),
        '--faq-subtitle-top': scaledPx(30),
        '--faq-subtitle-font': scaledPx(50),
        '--faq-subtitle-line': scaledPx(70),
        '--faq-description-width': scaledPx(760),
        '--faq-description-top': scaledPx(30),
        '--faq-description-font': scaledPx(24),
        '--faq-description-line': scaledPx(34),
        '--faq-list-top': scaledPx(109),
        '--faq-border-width': scaledPx(1),
        '--faq-item-bottom': scaledPx(30),
        '--faq-question-width': scaledPx(1120),
        '--faq-row-bottom': scaledPx(30),
        '--faq-question-font': scaledPx(20),
        '--faq-question-line': scaledPx(20),
        '--faq-icon-space': scaledPx(48),
        '--faq-icon-font': scaledPx(20),
        '--faq-icon-stroke': scaledPx(2),
        '--faq-answer-gap': scaledPx(20),
        '--faq-answer-padding': scaledPx(30),
        '--faq-answer-radius': scaledPx(20),
        '--faq-answer-font': scaledPx(20),
        '--faq-answer-line': scaledPx(30),
      }) as CSSProperties,
  )

  const updateScale = () => {
    const viewportWidth = Math.max(320, window.innerWidth || DESIGN_WIDTH)
    const nextScale = viewportWidth / DESIGN_WIDTH
    const nextRoundedScale = Number(nextScale.toFixed(5))
    const nextStageWidth = Math.round(DESIGN_WIDTH * nextScale)
    const nextStageHeight = Math.round(DESIGN_HEIGHT * nextScale)

    if (
      stageScale.value === nextRoundedScale &&
      stageWidth.value === nextStageWidth &&
      stageHeight.value === nextStageHeight
    ) {
      return
    }

    stageScale.value = nextRoundedScale
    stageWidth.value = nextStageWidth
    stageHeight.value = nextStageHeight
  }

  const scheduleScaleUpdate = () => {
    if (resizeFrame !== undefined) return

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = undefined
      updateScale()
    })
  }

  onMounted(() => {
    updateScale()
    window.addEventListener('resize', scheduleScaleUpdate)
    window.addEventListener('orientationchange', scheduleScaleUpdate)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', scheduleScaleUpdate)
    window.removeEventListener('orientationchange', scheduleScaleUpdate)

    if (resizeFrame !== undefined) {
      window.cancelAnimationFrame(resizeFrame)
    }
  })

  return {
    stageHeight,
    stageScale,
    stageVars,
    stageWidth,
    scaledPx,
  }
}
